<template>
  <div>
    <div v-show="successMessages.length">
      <p v-for="(message, index) in successMessages" :key="index" class="my-4 p-2 bg-app-green-200 border-2 border-app-green rounded-md">{{ message }}</p>
    </div>

    <FormKit v-show="successMessages.length === 0" v-slot="{ disabled, state: { loading } }" v-model="user" type="form" :actions="false" @submit="submitHandler">
      <ul class="w-full">
        <li>
          <h1 class="text-xl font-bold uppercase">{{ t_('title') }}</h1>
          <p class="mb-8 text-lg font-semibold leading-5">{{ t_('subtitle') }}</p>

          <ul>
            <!-- row -->
            <li class="mb-2 p-2">
              <ul class="grid gap-6 md:grid-cols-3">
                <li>
                  <FormKit
                    autocomplete="given-name"
                    type="text"
                    name="firstName"
                    :placeholder="t_('first-name')"
                    validation="required:trim|length:2,255"
                    :label="t_('first-name')"
                  />
                </li>
                <li>
                  <FormKit type="text" name="insertion" :placeholder="t_('insertion')" :label="t_('insertion')" />
                </li>
                <li>
                  <FormKit autocomplete="family-name" type="text" name="lastName" :placeholder="t_('last-name')" validation="required:trim|length:2,255" :label="t_('last-name')" />
                </li>
              </ul>
            </li>
            <li class="mb-2 p-2">
              <ul class="grid">
                <li class="inline-radio">
                  <FormKit
                    autocomplete="sex"
                    name="gender"
                    type="radio"
                    :label="t_('gender')"
                    :options="[
                      { value: 'female', label: t_('female') },
                      { value: 'male', label: t_('male') },
                      { value: 'notRelevant', label: t_('gender-irrelevant') },
                    ]"
                  />
                </li>
              </ul>
            </li>
            <li class="mb-2 p-2">
              <ul class="grid gap-6 md:grid-cols-2">
                <li>
                  <FormKit autocomplete="postal-code" type="text" name="zipCode" :placeholder="t_('zipcode')" validation="required:trim|length:6" :label="t_('zipcode')" />
                </li>
                <li>
                  <FormKit type="text" name="houseNumber" :placeholder="t_('house-number')" validation="required:trim|length:0,12" class="mb-2" :label="t_('house-number')" />
                </li>
              </ul>
            </li>
            <li class="mb-2 p-2">
              <ul class="grid gap-6 md:grid-cols-2">
                <li>
                  <FormKit autocomplete="address-line1" type="text" name="street" :placeholder="t_('street')" validation="required:trim|length:3" :label="t_('street')" />
                </li>
                <li>
                  <FormKit type="text" name="city" :placeholder="t_('city')" validation="required:trim|length:3" :label="t_('city')" />
                </li>
              </ul>
            </li>
            <li class="mb-2 p-2">
              <ul class="grid gap-6 md:grid-cols-2">
                <li>
                  <FormKit autocomplete="email" type="text" name="email" :placeholder="t_('email')" validation="required:trim|email|length:6" :label="t_('email')" />
                </li>
                <li>
                  <FormKit autocomplete="tel" type="text" name="telephone" :placeholder="t_('telephone')" validation="required:trim|length:0,20" :label="t_('telephone')" />
                </li>
              </ul>
            </li>
            <li class="mb-2 p-2">
              <ul class="grid gap-6 md:grid-cols-2">
                <li>
                  <FormKit autocomplete="new-password" type="password" name="password" :placeholder="t_('password')" validation="required:trim|length:6" :label="t_('password')" />
                </li>
                <li>
                  <FormKit
                    autocomplete="new-password"
                    type="password"
                    name="passwordConfirm"
                    :placeholder="t_('password-confirm')"
                    validation="required:trim|confirm:password"
                    :label="t_('password-confirm')"
                  />
                </li>
              </ul>
            </li>
            <li class="mb-2 p-3">
              <ul class="grid gap-6 md:grid-cols-3">
                <li class="inline-radio">
                  <FormKit name="hasMembership" :options="{ yes: t_('yes'), no: t_('no') }" :label="t_('is-member')" type="radio" />
                </li>
                <li>
                  <FormKit type="checkbox" name="hasNewsletter" :label="t_('newsletter')" />
                </li>
              </ul>
            </li>
          </ul>
        </li>
      </ul>

      <div v-show="formError">
        <hr class="my-4 border-t-2 border-solid border-app-green-100" />
        <FormKitMessages />
      </div>

      <hr class="my-4 border-t-2 border-solid border-app-green-100" />

      <section class="container grid grid-cols-1 px-12 md:px-5 lg:grid-cols-3">
        <div class="mb-4">
          <FormKit type="checkbox" name="hasMarketingConsent" :label="t_('consent')" />
        </div>

        <p v-if="t_('notice').length > 3" class="mb-4 lg:col-end-3 lg:col-start-1">** {{ t_('notice') }}</p>
        <div class="flex lg:col-end-4 lg:col-start-3">
          <FormKit type="submit" :disabled="loading || disabled" class="group inline-block mb-6">
            {{ t_('register') }}
            <AtomIcon
              :icon="loading || disabled ? 'app-loader' : 'arrow-right'"
              class="ml-8 w-5 h-5"
              :class="{ 'transform group-hover:translate-x-1 transition duration-200': !(loading || disabled) }"
            />
          </FormKit>
        </div>
      </section>
    </FormKit>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { FormKitMessages } from '@formkit/vue'
import { setupTranslationPrefixer } from '~/composables/util'
import { setupUser } from '~/composables/user'

const getDummyUser = () => ({
  firstName: 'Fabrique',
  lastName: 'Test',
  gender: 'male',
  zipCode: '3013 AK',
  houseNumber: '45',
  street: 'Stationsplein',
  city: 'Rotterdam',
  telephone: '06123456789',
  email: `support+${Math.floor(Math.random() * 10000)}@fabrique.nl`,
  password: 'secret',
  passwordConfirm: 'secret',
  hasMembership: 'yes',
})

const { t_ } = setupTranslationPrefixer('UserRegisterForm')
const { transformFormToData, register } = setupUser()
const formError = ref<boolean>(false)
const successMessages = ref([])
const user = ref(import.meta.dev ? getDummyUser() : {})

const submitHandler = async (_data: object, node: any) => {
  // Clear errors and successes before saving
  formError.value = false
  node.clearErrors()
  successMessages.value = []

  const { success, messages } = await register(transformFormToData(user))
  if (success) {
    successMessages.value = [t_('generic-success')]
  } else {
    formError.value = true
    node.setErrors(messages.length ? messages : [t_('generic-error')])
  }
}
</script>

<style lang="scss" scoped>
:deep(.formkit-outer) {
  margin: 0;
}
:deep([data-type='radio']) {
  .formkit-options .formkit-option {
    @apply inline-block mr-4;
  }
}
</style>
